import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import {
  candidateFromFile,
  selectionFromDrop,
  selectionFromFiles,
} from '../../knowledge/file-adapter';
import type { LocalSelection } from '../../knowledge/ports';
import type { SovereignProject } from '../../sovereign/domain';
import { noemirProductVersion } from '../../release/version';

interface FirstEntryProps {
  readonly onEnter: (project: SovereignProject) => Promise<void>;
}

const accepted = '.noemir,.md,.markdown,.txt,.pdf,.csv,.json';

export const FirstEntry = ({ onEnter }: FirstEntryProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const vaultRef = useRef<HTMLInputElement>(null);
  const pasteRef = useRef<HTMLTextAreaElement>(null);
  const [openOptions, setOpenOptions] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    vaultRef.current?.setAttribute('webkitdirectory', '');
    vaultRef.current?.setAttribute('directory', '');
  }, []);

  const ingestSelection = async (selection: LocalSelection): Promise<void> => {
    setBusy(true);
    setStatus('Reading local files');
    try {
      const [{ LocalIngestionPipeline }, { createProjectFromRecords }] = await Promise.all([
        import('../../knowledge/pipeline'),
        import('../../release/project-factory'),
      ]);
      if (selection.candidates.length === 0)
        throw new Error('No supported local files were selected.');
      const result = await new LocalIngestionPipeline().ingest(selection, {
        signal: new AbortController().signal,
        onProgress: (progress) =>
          setStatus(
            `${progress.phase.replaceAll('_', ' ')} · ${String(progress.completedFiles)}/${String(progress.totalFiles)}`,
          ),
      });
      if (result.record === null)
        throw new Error(result.issues[0]?.message ?? 'Local import failed.');
      await onEnter(createProjectFromRecords([result.record]));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Local import failed.');
      setBusy(false);
    }
  };

  const ingestFiles = async (files: FileList, folder: boolean): Promise<void> => {
    if (!folder && files.length === 1 && files[0]?.name.toLocaleLowerCase().endsWith('.noemir')) {
      setBusy(true);
      setStatus('Verifying local .noemir archive');
      try {
        const { importNoemirArchive } = await import('../../sovereign/archive');
        await onEnter(await importNoemirArchive(files[0]));
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'The .noemir archive could not open.');
        setBusy(false);
      }
      return;
    }
    await ingestSelection(
      selectionFromFiles(files, folder ? 'folder_picker' : 'file_picker', folder),
    );
  };

  const acceptDrop = async (event: DragEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();
    setDropActive(false);
    const archive = [...event.dataTransfer.files].find((file) =>
      file.name.toLocaleLowerCase().endsWith('.noemir'),
    );
    if (event.dataTransfer.files.length === 1 && archive !== undefined) {
      setBusy(true);
      setStatus('Verifying local .noemir archive');
      try {
        const { importNoemirArchive } = await import('../../sovereign/archive');
        await onEnter(await importNoemirArchive(archive));
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'The .noemir archive could not open.');
        setBusy(false);
      }
      return;
    }
    await ingestSelection(await selectionFromDrop(event.dataTransfer));
  };

  const acceptFiles = (event: ChangeEvent<HTMLInputElement>, folder: boolean): void => {
    const files = event.currentTarget.files;
    event.currentTarget.value = '';
    if (files !== null && files.length > 0) void ingestFiles(files, folder);
  };

  const ingestPaste = async (): Promise<void> => {
    const content = pasteRef.current?.value.trim() ?? '';
    if (content.length === 0) {
      setStatus('Paste text or Markdown before opening it.');
      return;
    }
    const file = new File([content], 'pasted-note.md', { type: 'text/markdown' });
    const selection = {
      candidates: [candidateFromFile(file)],
      containerKind: 'file' as const,
      origin: 'file_picker' as const,
    };
    setBusy(true);
    setStatus('Parsing pasted Markdown locally');
    try {
      const [{ LocalIngestionPipeline }, { createProjectFromRecords }] = await Promise.all([
        import('../../knowledge/pipeline'),
        import('../../release/project-factory'),
      ]);
      const result = await new LocalIngestionPipeline().ingest(selection, {
        signal: new AbortController().signal,
      });
      if (result.record === null) throw new Error('Pasted content could not be parsed.');
      await onEnter(createProjectFromRecords([result.record]));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Pasted content could not be parsed.');
      setBusy(false);
    }
  };

  return (
    <main className="first-entry" aria-labelledby="first-entry-title">
      <div className="first-entry-core">
        <p className="first-entry-mark">N</p>
        <h1 id="first-entry-title">NOEMIR</h1>
        <p className="first-entry-subtitle">LOCAL INTELLIGENCE SYSTEM</p>
        <div className="first-entry-actions">
          <button
            className="entry-action"
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setStatus('Opening clearly marked example system');
              void import('../../release/sample-project')
                .then(({ createExampleProject }) => onEnter(createExampleProject()))
                .catch(() => {
                  setBusy(false);
                  setStatus('The example system could not be opened.');
                });
            }}
          >
            EXPLORE A SYSTEM
          </button>
          <button
            className="entry-action"
            type="button"
            aria-expanded={openOptions}
            disabled={busy}
            onClick={() => setOpenOptions((current) => !current)}
          >
            OPEN YOURS
          </button>
        </div>

        {openOptions ? (
          <section
            className="entry-open-options"
            aria-label="Open your local system"
            data-drop-active={dropActive}
            onDragEnter={(event) => {
              event.preventDefault();
              setDropActive(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                setDropActive(false);
            }}
            onDrop={(event) => void acceptDrop(event)}
          >
            <input
              ref={fileRef}
              hidden
              type="file"
              multiple
              accept={accepted}
              onChange={(event) => acceptFiles(event, false)}
            />
            <input
              ref={vaultRef}
              hidden
              type="file"
              multiple
              onChange={(event) => acceptFiles(event, true)}
            />
            <div className="entry-option-row">
              <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
                FILES / .NOEMIR
              </button>
              <button type="button" disabled={busy} onClick={() => vaultRef.current?.click()}>
                OBSIDIAN VAULT
              </button>
              <button
                type="button"
                disabled={busy}
                aria-expanded={pasteOpen}
                onClick={() => setPasteOpen((current) => !current)}
              >
                PASTE TEXT
              </button>
            </div>
            <p className="entry-drop-hint">DROP LOCAL FILES OR A VAULT HERE</p>
            {pasteOpen ? (
              <div className="entry-paste">
                <label htmlFor="entry-paste-content">Text or Markdown</label>
                <textarea ref={pasteRef} id="entry-paste-content" rows={7} autoFocus />
                <button
                  type="button"
                  className="entry-paste-open"
                  disabled={busy}
                  onClick={() => void ingestPaste()}
                >
                  OPEN LOCALLY
                </button>
              </div>
            ) : null}
          </section>
        ) : null}
        <p className="entry-privacy">LOCAL BY DEFAULT · NO ACCOUNT · NO UPLOAD</p>
        <p className="entry-version">v{noemirProductVersion}</p>
        <p className="entry-status" role="status" aria-live="polite">
          {status}
        </p>
      </div>
    </main>
  );
};
